import { PspRules } from "@CommonTypes/backend/PspRules";
import { verticalBlockingPreset } from "src/modals/StorePlanogramRules/store/verticalBlockingPreset";

/**
 * Seeded into the heavy-rule form, not into `defaultPspRules.heavy_rule` — the
 * rule is null until the user supplies `max_deck_y`, which has no default worth
 * guessing. Lives here so every weight number the UI seeds is in one file.
 */
export const DEFAULT_HEAVY_ITEM_THRESHOLD_G = 4536;

/**
 * Integer GRAMS, matching pogger's contract exactly — the lb/cm translation
 * layer that used to sit in the backend is what drifted out of sync with it.
 * 68039 g = 150 lb is the merchant's standing assumption (Wendy, 2026-08-23),
 * seeded as a form default the user can change.
 *
 * Declared as its own non-optional constant (rather than read off
 * `defaultPspRules.weight_rules`) because `weight_rules` is optional on
 * `PspRules` for backend-compatibility reasons — any caller that needs a
 * guaranteed value should import this directly instead of re-deriving a
 * fallback.
 */
export const DEFAULT_WEIGHT_RULES: NonNullable<PspRules["weight_rules"]> = {
  default_shelf_weight_limit: 68039,
  heavy_rule: null,
  resolve_overweight_shelves: false,
  swap_heavy_brands_below_max_height: false,
  heavy_brand_block_level: "cdt2",
  swap_fill_floor_pct: 0.9,
  // Not user-facing: the backend forces it on whenever the merchant gate is
  // enabled, since the report is the screen's only source of shelf limits.
  report: false,
};

export const defaultPspRules: PspRules = {
  fixture: {
    can_adjust_shelf_notches: true,
    can_add_or_delete_shelves: false,
    shelves_same_height_as_base_pog: true,
    vertical_overlap: false,
    merged_shelf_width_tolerance_pct: 10,
    vertical_split_section: 1,
  },
  range: {
    can_add_non_core_range_products: true,
    can_apply_squeeze_to_placed_products: false,
    balance_facings_mode: "EMPTY_SPACE_ONLY",
    adjust_facings_mode: "EXACT_MATCH",
  },
  merchandising: {
    vertical_blocking_mode: true,
    can_break_cases_trays_if_core_range_not_fit: true,
    can_fill_extra_space_next_to_cases_trays_with_loose_products: false,
    how_should_loose_products_be_placed_with_respect_to_their_cases_trays: "BEHIND",
    leverage_alternative_orientations: true,
    extra_facing_marginal_value: verticalBlockingPreset.extraFacingMarginalValue,
    snaking: verticalBlockingPreset.snaking,
    cdt_stacking: verticalBlockingPreset.cdtStacking,
    items_squeeze_option: "none",
  },
  high_density_mode: {
    enabled: true,
    snaking_strategy: verticalBlockingPreset.snakingStrategy,
    variant_contiguous: true,
    place_all_variants: verticalBlockingPreset.placeAllVariants,
    allow_breaking_variant_sequencing: false,
    vertical_blocking_mode: true,
    strict_rectangles: true,
  },
  high_density_building_blocks_mode: {
    enabled: false,
    direction: "BACKWARD",
    start_from: { type: "BIGGEST" },
  },
  mudmap_level: verticalBlockingPreset.mudmapLevel,
  optimize_on: "SALES",
  relax_factors: {
    space_mix: verticalBlockingPreset.spaceMix,
    either_or: 1,
    both_or_none: 1,
    prereq: "inf",
    shelf_item_count: 1,
    fixed_notch_num: verticalBlockingPreset.fixedNotchNum,
    finger_space: 5,
    units_not_enough_dos: verticalBlockingPreset.unitsNotEnoughDos,
    units_not_enough_mos: verticalBlockingPreset.unitsNotEnoughMos,
    units_too_many: 0,
    facings_not_enough: 0,
    facings_too_many: verticalBlockingPreset.facingsTooMany,
    min_width: 0.5,
    min_distribution: verticalBlockingPreset.minDistribution,
    width_slack: 0.1,
    cdt1_contiguous: verticalBlockingPreset.cdt1Contiguous,
    cdt2_contiguous: verticalBlockingPreset.cdt2Contiguous,
    cdt3_contiguous: verticalBlockingPreset.cdt3Contiguous,
    cdt1_order: verticalBlockingPreset.cdt1Order,
    cdt2_order: verticalBlockingPreset.cdt2Order,
    cdt3_order: verticalBlockingPreset.cdt3Order,
    item_id_order: verticalBlockingPreset.itemIdOrder,
    item_span: verticalBlockingPreset.itemSpan,
    cdt1_span: 0,
    cdt2_span: 0,
    cdt3_span: 0.2,
    secondary_orientation: 0.3,
    rows_not_enough: 10,
    shelf_segment_space: verticalBlockingPreset.shelfSegmentSpace,
  },
  mudmap_generation_penalty_multiplier: {
    totalSpaceSlack: 100,
    cdt0SectionsSpanning: 10,
    cdt0ShelvesSpanning: 10,
    cdt2SectionsSpanning: 1,
    cdt2ShelvesSpanning: 1,
    cdt0RoundingSlack: 20,
    cdt2RoundingSlack: 20,
    cdt1BreakingSequencing: 100,
    cdt2BreakingSequencing: 100,
    cdtSpaceMixSlack: 100,
    cdt0ShelvesMisaligned: verticalBlockingPreset.cdt0ShelvesMisaligned,
    itemWidthSatisfied: 1,
    cdt0ShelfSpaceMisalignment: verticalBlockingPreset.cdt0ShelfSpaceMisalignment,
    cdt2ShelfSpaceMisalignment: verticalBlockingPreset.cdt2ShelfSpaceMisalignment,
    verticalBlockShelfPresence: 100,
    cdt0CellXMisalignment: verticalBlockingPreset.cdt0CellXMisalignment,
    cdt2CellXMisalignment: verticalBlockingPreset.cdt2CellXMisalignment,
  },
  weight_rules: DEFAULT_WEIGHT_RULES,
};
